import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api.ts';

/* Inline rather than a dependency: two icons do not justify an icon package,
   and they inherit currentColor so they follow the theme without extra CSS. */
function Eye() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M1.8 12S5.4 5.4 12 5.4 22.2 12 22.2 12 18.6 18.6 12 18.6 1.8 12 1.8 12Z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function EyeOff() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M9.9 5.7A9.6 9.6 0 0 1 12 5.4c6.6 0 10.2 6.6 10.2 6.6a17 17 0 0 1-3.4 4.2M6.3 7.9A17 17 0 0 0 1.8 12S5.4 18.6 12 18.6a9.7 9.7 0 0 0 4-.85" />
      <path d="M10 10a3.2 3.2 0 0 0 4.3 4.3" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

/**
 * Owner login (FR-4.1).
 *
 * The two-factor field appears only once the server says it is needed, so an
 * owner without 2FA never sees a field they cannot fill, and one with it is
 * not asked for a code before their password has been accepted.
 */

export function Login({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);

  useEffect(() => {
    void api
      .authStatus()
      .then((s) => setSetupRequired(s.setupRequired))
      .catch(() => undefined);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.login(username, password, totpCode || undefined);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.body.totpRequired) {
        setTotpRequired(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="brand">
          Arbitrage<span>.</span>
        </div>
        <div className="tagline">Private single-operator tool</div>

        {setupRequired ? (
          <div className="card">
            <div className="card-body">
              <div className="notice warn" style={{ marginBottom: 0 }}>
                <strong>No owner account exists yet.</strong>
                <br />
                Create one on the server with:
                <br />
                <code className="mono">pnpm --filter @arb/api setup</code>
              </div>
            </div>
          </div>
        ) : (
          <form className="card" onSubmit={submit}>
            <div className="card-body">
              <div className="field">
                <label>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                />
              </div>
              <div className="field">
                <label>Password</label>
                <div className="input-with-action">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="reveal"
                    onClick={() => setShowPassword((v) => !v)}
                    // A single-operator tool on a private screen: the risk of a
                    // shoulder-surfer is smaller than the risk of locking the
                    // only account out over a typo in a generated password.
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>

              {totpRequired && (
                <div className="field">
                  <label>Two-factor code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
              )}

              {error && <div className="notice bad">{error}</div>}

              <button
                className="btn primary"
                type="submit"
                disabled={submitting || !username || !password}
                style={{ width: '100%' }}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
